// src/components/BulkImportProducts.js
import { useState, useRef } from 'react'
import { supabase } from '../supabase'

const SAMPLE_CSV = `name,product_code,category,sell_price,buy_price,stock,status,variant_name,variant_1_type,variant_1_value,variant_2_type,variant_2_value,size,color,weight_kg,product_type,unit_type,season,notes
Amara Kameez,CLT-AK-BLU-977,Clothing,850,500,20,active,Blue,Color,Blue,,,S/M/L/XL,Blue,,Clothing,piece,Summer,
Amara Kameez,CLT-AK-RED-977,Clothing,850,500,15,active,Red,Color,Red,,,S/M/L/XL,Red,,Clothing,piece,Summer,
Amara Kameez,CLT-AK-GRN-977,Clothing,800,480,10,active,Green,Color,Green,,,S/M/L/XL,Green,,Clothing,piece,Summer,
Gold Bracelet,JWL-GBRC-22K-456,Jewelry,12000,9000,5,active,22K,Karat,22K,,,,,35,Jewelry,piece,,
Gold Bracelet,JWL-GBRC-18K-456,Jewelry,9000,7000,8,active,18K,Karat,18K,,,,,30,Jewelry,piece,,
Mango Juice,FDB-MGO-250ML-103,Food & Beverage,120,80,100,active,250ml,Volume,250ml,,,,,0.3,,ml,,
Mango Juice,FDB-MGO-500ML-103,Food & Beverage,200,130,80,active,500ml,Volume,500ml,,,,,0.6,,ml,,
Plain White T-Shirt,CLT-PWT-977,Clothing,350,200,50,active,,,,,,S/M/L/XL,White,,Clothing,piece,,No color variant`

const REQUIRED = ['name','product_code','category','sell_price','buy_price','stock','status']

const COLUMN_GUIDE = [
  { col:'name',            req:true,  desc:'Full product name. All variant rows of the same product must share the exact same name — this is how the system groups them.',  example:'Amara Kameez' },
  { col:'product_code',    req:true,  desc:'Unique code per variant row. Formula: CAT-NAME-VARIANT-NUM. Each row must have a different code. See naming guide in the modal.', example:'CLT-AK-BLU-977' },
  { col:'category',        req:true,  desc:'Main product group for filtering and analytics',                     example:'Clothing, Food, Jewelry' },
  { col:'sell_price',      req:true,  desc:'Selling price — numbers only, no currency symbol',                   example:'850' },
  { col:'buy_price',       req:true,  desc:'Your cost price — used for margin calculations',                     example:'500' },
  { col:'stock',           req:true,  desc:'Stock quantity for this specific variant row',                       example:'20' },
  { col:'status',          req:true,  desc:'Must be exactly: active or inactive',                                example:'active' },
  { col:'variant_name',    req:false, desc:'Variant label shown to customer on order form. Leave blank if no variants.', example:'Blue, 100ml, 22K-10g, Design 1' },
  { col:'variant_1_type',  req:false, desc:'First variant dimension type',                                       example:'Color, Weight, Karat, Design, Material' },
  { col:'variant_1_value', req:false, desc:'First variant dimension value',                                      example:'Blue, 100g, 22K, Design 1, Cotton' },
  { col:'variant_2_type',  req:false, desc:'Second variant dimension type (only if 2 dimensions)',               example:'Material, Volume' },
  { col:'variant_2_value', req:false, desc:'Second variant dimension value',                                     example:'Cotton, 250ml' },
  { col:'size',            req:false, desc:'Clothing size — sub-option within a variant, does not affect product code', example:'S, M, L, XL, XXL' },
  { col:'color',           req:false, desc:'Searchable color field for inventory filtering',                     example:'Red, Blue, White' },
  { col:'weight_kg',       req:false, desc:'Physical weight in kg for courier charge calculations',              example:'0.5' },
  { col:'product_type',    req:false, desc:'Only for special modes. Leave blank for regular products.',          example:'gold, medicine, service' },
  { col:'unit_type',       req:false, desc:'Unit of measurement. Defaults to piece if blank.',                   example:'piece, kg, ml, meter, set' },
  { col:'season',          req:false, desc:'Season tag for filtering and promotions',                            example:'Summer, Eid, Winter' },
  { col:'notes',           req:false, desc:'Internal notes — not shown to customers',                           example:'Imported from old system' },
]

const CAT_CODES = [
  { code:'CLT', label:'Clothing' }, { code:'JWL', label:'Jewelry' },
  { code:'MED', label:'Medicine' }, { code:'BTY', label:'Beauty' },
  { code:'FDB', label:'Food & Beverage' }, { code:'ELC', label:'Electronics' },
  { code:'HME', label:'Home & Living' }, { code:'SPT', label:'Sports' },
  { code:'TOY', label:'Toys' }, { code:'SVC', label:'Service' },
]

const CODE_EXAMPLES = [
  { code:'CLT-AK-BLU-977',     desc:'Clothing, Amara Kameez, Blue' },
  { code:'CLT-AK-BLU-CTN-977', desc:'Clothing, Amara Kameez, Blue, Cotton' },
  { code:'JWL-GBRC-22K-456',   desc:'Jewelry, Gold Bracelet, 22K' },
  { code:'MED-PARA-20G-234',   desc:'Medicine, Paracetamol, 20g' },
  { code:'BTY-FCR-100ML-512',  desc:'Beauty, Face Cream, 100ml' },
  { code:'FDB-MGO-250ML-103',  desc:'Food, Mango Juice, 250ml' },
  { code:'CLT-AK-977',         desc:'Clothing, Amara Kameez, No variant' },
]
const VALID_STATUSES = ['active','inactive']

function parseCSV(text) {
  const lines = text.trim().split('\n').filter(Boolean)
  if (lines.length < 2) return { headers:[], rows:[] }
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g,'_'))
  const rows = lines.slice(1).map((line,i) => {
    const values = line.split(',').map(v => v.trim())
    const row = { _rowNum: i+2 }
    headers.forEach((h,j) => { row[h] = values[j] || '' })
    return row
  })
  return { headers, rows }
}

function validateRow(row, existingCodes) {
  const errors = [], warnings = []
  REQUIRED.forEach(f => { if (!row[f]||row[f].trim()==='') errors.push(`Missing: ${f}`) })
  if (row.sell_price && isNaN(Number(row.sell_price))) errors.push('sell_price must be a number')
  if (row.buy_price  && isNaN(Number(row.buy_price)))  errors.push('buy_price must be a number')
  if (row.stock      && isNaN(Number(row.stock)))      errors.push('stock must be a number')
  if (row.status && !VALID_STATUSES.includes(row.status.toLowerCase())) errors.push("status must be 'active' or 'inactive'")
  if (!row.variant_name||row.variant_name.trim()==='') warnings.push('No variant — will import as simple product')
  const isDuplicate = existingCodes.has(row.product_code?.trim())
  return { errors, warnings, isDuplicate }
}

const ov = { position:'fixed',inset:0,background:'rgba(15,23,42,0.7)',backdropFilter:'blur(4px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:400,padding:'16px' }
const md = { background:'var(--bg-card)',borderRadius:'var(--radius-xl)',width:'100%',maxWidth:'900px',maxHeight:'90vh',display:'flex',flexDirection:'column',border:'1px solid var(--border)',boxShadow:'0 24px 64px rgba(0,0,0,0.3)',fontFamily:'var(--font-ui)',overflow:'hidden' }
const btn = (bg,col='white') => ({ padding:'10px 20px',borderRadius:'var(--radius-md)',border:'none',background:bg,color:col,fontSize:'13px',fontWeight:700,cursor:'pointer',fontFamily:'var(--font-ui)',display:'flex',alignItems:'center',gap:'6px' })
const obtn = { padding:'10px 20px',borderRadius:'var(--radius-md)',border:'1px solid var(--border)',background:'var(--bg-card)',color:'var(--text-primary)',fontSize:'13px',fontWeight:600,cursor:'pointer',fontFamily:'var(--font-ui)' }


const STEPS = ['Upload','Preview & Validate','Confirm','Done']

export default function BulkImportProducts({ seller, onClose, onSuccess }) {
  const [step,       setStep]       = useState(0)
  const [validated,  setValidated]  = useState([])
  const [removed,    setRemoved]    = useState(new Set())
  const [editRow,    setEditRow]    = useState(null)
  const [editVals,   setEditVals]   = useState({})
  const [importing,  setImporting]  = useState(false)
  const [progress,   setProgress]   = useState(0)
  const [result,     setResult]     = useState(null)
  const fileRef = useRef()

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV],{type:'text/csv'})
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href=url; a.download='pageconnect-products-sample.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const handleFile = async (file) => {
    const text = await file.text()
    const { rows } = parseCSV(text)
    const { data:existing } = await supabase.from('products').select('product_code').eq('seller_id',seller.id)
    const existingCodes = new Set((existing||[]).map(p=>p.product_code))
    setValidated(rows.map(r=>({...r, _validation:validateRow(r,existingCodes)})))
    setStep(1)
  }

  const activeRows = validated.filter(r=>!removed.has(r._rowNum))
  const errorRows  = activeRows.filter(r=>r._validation.errors.length>0)
  const dupeRows   = activeRows.filter(r=>r._validation.isDuplicate)
  const validRows  = activeRows.filter(r=>r._validation.errors.length===0 && !r._validation.isDuplicate)
  const canProceed = errorRows.length===0 && dupeRows.length===0 && activeRows.length>0

  const removeRow = (n) => setRemoved(p=>new Set([...p,n]))
  const removeAllDupes   = () => setRemoved(p=>new Set([...p,...dupeRows.map(r=>r._rowNum)]))
  const removeAllErrors  = () => setRemoved(p=>new Set([...p,...errorRows.map(r=>r._rowNum)]))

  const startEdit = (row) => { setEditRow(row._rowNum); setEditVals({...row}) }
  const saveEdit  = async () => {
    const { data:ex } = await supabase.from('products').select('product_code').eq('seller_id',seller.id)
    const ec = new Set((ex||[]).map(p=>p.product_code))
    setValidated(p=>p.map(r=>r._rowNum===editRow ? {...editVals,_rowNum:r._rowNum,_validation:validateRow(editVals,ec)} : r))
    setEditRow(null)
  }

  const groupByProduct = (rows) => {
    const map = {}
    rows.forEach(row => {
      const key = row.name?.trim().toLowerCase()
      if (!map[key]) map[key] = { product:row, variants:[] }
      if (row.variant_name?.trim()) map[key].variants.push(row)
    })
    return Object.values(map)
  }

  const handleImport = async () => {
    setImporting(true); setProgress(0)
    const groups = groupByProduct(validRows)
    let imported=0, failed=0; const errors=[]
    for (let i=0; i<groups.length; i++) {
      const { product:p, variants } = groups[i]
      setProgress(Math.round((i/groups.length)*100))
      try {
        const { data:newProd, error:pErr } = await supabase.from('products').insert({
          seller_id:seller.id, name:p.name?.trim(), product_code:p.product_code?.trim(),
          category:p.category?.trim()||null, sell_price:Number(p.sell_price)||null,
          buy_price:Number(p.buy_price)||null, stock:variants.length>0?0:(Number(p.stock)||0),
          status:p.status?.toLowerCase()||'active', color:p.color?.trim()||null,
          weight_kg:p.weight_kg?Number(p.weight_kg):null, product_type:p.product_type?.trim()||null,
          unit_type:p.unit_type?.trim()||'piece', season:p.season?.trim()||null,
          notes:p.notes?.trim()||null, advance_payment_type:'none', source:'bulk_import',
        }).select().single()
        if (pErr) throw pErr
        if (variants.length>0) {
          const { error:vErr } = await supabase.from('product_variants').insert(
            variants.map(v=>({
              product_id:newProd.id, seller_id:seller.id,
              variant_name:v.variant_name?.trim(), variant_code:v.variant_code?.trim()||null,
              variant_1_type:v.variant_1_type?.trim()||null, variant_1_value:v.variant_1_value?.trim()||null,
              variant_2_type:v.variant_2_type?.trim()||null, variant_2_value:v.variant_2_value?.trim()||null,
              stock:Number(v.stock)||0, sell_price:Number(v.sell_price)||null,
              buy_price:Number(v.buy_price)||null, weight_kg:v.weight_kg?Number(v.weight_kg):null,
            }))
          )
          if (vErr) throw vErr
        }
        imported++
      } catch(e) { failed++; errors.push(`Row ${p._rowNum} (${p.name}): ${e.message}`) }
    }
    setProgress(100); setResult({imported,failed,errors,skipped:removed.size})
    setImporting(false); setStep(3)
    if (imported>0) onSuccess?.()
  }

  return (
    <div style={ov} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={md}>

        {/* Header */}
        <div style={{padding:'18px 24px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
          <div>
            <div style={{fontSize:'16px',fontWeight:700,color:'var(--text-primary)'}}>Bulk Import Products</div>
            <div style={{fontSize:'12px',color:'var(--text-muted)',marginTop:'2px'}}>One row per variant · Rows grouped by product_code</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',fontSize:'20px',color:'var(--text-muted)'}}>×</button>
        </div>

        {/* Steps */}
        <div style={{padding:'10px 24px',borderBottom:'1px solid var(--border)',display:'flex',gap:'20px',background:'var(--bg-muted)',flexShrink:0}}>
          {STEPS.map((label,i)=>(
            <div key={i} style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'12px',fontWeight:step===i?700:500,color:step===i?'var(--primary)':step>i?'var(--success)':'var(--text-muted)'}}>
              <div style={{width:'20px',height:'20px',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'10px',fontWeight:700,background:step===i?'var(--primary)':step>i?'var(--success)':'var(--border)',color:step>=i?'white':'var(--text-muted)',flexShrink:0}}>
                {step>i?'✓':i+1}
              </div>
              {label}
            </div>
          ))}
        </div>

        {/* Body */}
        <div style={{flex:1,overflowY:'auto',padding:'20px 24px'}}>

          {/* STEP 0 — Upload */}
          {step===0&&(
            <div>
              <div style={{padding:'16px',borderRadius:'var(--radius-lg)',background:'#FFFBEB',border:'1px solid #FDE68A',marginBottom:'20px'}}>
                <div style={{fontSize:'13px',fontWeight:700,color:'#92400E',marginBottom:'6px'}}>⚠ Read before uploading</div>
                <div style={{fontSize:'12.5px',color:'#92400E',lineHeight:1.7}}>
                  Bulk import is for <strong>historical or batch data only</strong>. For day-to-day products, add them one by one through Inventory → Add Product. Every required field must be filled — incomplete rows will be rejected with no exceptions.
                </div>
              </div>

              {/* Column guide table */}
              <div style={{marginBottom:'20px'}}>
                <div style={{fontSize:'13px',fontWeight:700,color:'var(--text-primary)',marginBottom:'10px'}}>Column guide — read this before filling your CSV:</div>
                <div style={{border:'1px solid var(--border)',borderRadius:'var(--radius-md)',overflow:'hidden'}}>
                  <div style={{display:'grid',gridTemplateColumns:'160px 80px 1fr 160px',padding:'8px 12px',background:'var(--bg-muted)',borderBottom:'1px solid var(--border)',fontSize:'10px',fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',gap:'12px'}}>
                    <div>Column</div><div>Required</div><div>Description</div><div>Example</div>
                  </div>
                  <div style={{maxHeight:'320px',overflowY:'auto'}}>
                    {COLUMN_GUIDE.map((c,i)=>(
                      <div key={c.col} style={{display:'grid',gridTemplateColumns:'160px 80px 1fr 160px',padding:'9px 12px',borderBottom:'1px solid var(--border)',background:i%2===0?'var(--bg-card)':'var(--bg-muted)',gap:'12px',alignItems:'center'}}>
                        <div style={{fontFamily:'var(--font-mono)',fontSize:'11.5px',fontWeight:600,color:c.req?'var(--error)':'var(--primary)'}}>{c.col}</div>
                        <div>
                          <span style={{fontSize:'10px',fontWeight:700,padding:'2px 7px',borderRadius:'var(--radius-full)',background:c.req?'var(--error-light)':'var(--success-light)',color:c.req?'var(--error)':'var(--success)'}}>
                            {c.req?'Required':'Optional'}
                          </span>
                        </div>
                        <div style={{fontSize:'12px',color:'var(--text-muted)',lineHeight:1.5}}>{c.desc}</div>
                        <div style={{fontSize:'11px',fontFamily:'var(--font-mono)',color:'var(--text-muted)',background:'var(--bg-muted)',padding:'3px 7px',borderRadius:'4px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.example}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Product code naming guide */}
              <div style={{marginBottom:'20px'}}>
                <div style={{fontSize:'13px',fontWeight:700,color:'var(--text-primary)',marginBottom:'10px'}}>Product code naming guide:</div>
                <div style={{padding:'14px 16px',borderRadius:'var(--radius-md)',background:'var(--bg-muted)',border:'1px solid var(--border)',marginBottom:'10px'}}>
                  <div style={{fontSize:'12px',fontWeight:700,color:'var(--text-primary)',marginBottom:'8px'}}>Formula: <span style={{fontFamily:'var(--font-mono)',color:'var(--primary)'}}>CAT-NAME-VARIANT1-VARIANT2-NUM</span></div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'6px',marginBottom:'12px'}}>
                    {CAT_CODES.map(c=>(
                      <div key={c.code} style={{padding:'6px 8px',borderRadius:'6px',background:'var(--bg-card)',border:'1px solid var(--border)',textAlign:'center'}}>
                        <div style={{fontFamily:'var(--font-mono)',fontSize:'11px',fontWeight:700,color:'var(--primary)'}}>{c.code}</div>
                        <div style={{fontSize:'10px',color:'var(--text-muted)',marginTop:'2px'}}>{c.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{fontSize:'11px',fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'6px'}}>Examples:</div>
                  {CODE_EXAMPLES.map(e=>(
                    <div key={e.code} style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'4px'}}>
                      <span style={{fontFamily:'var(--font-mono)',fontSize:'12px',fontWeight:700,color:'var(--primary)',flexShrink:0}}>{e.code}</span>
                      <span style={{fontSize:'11.5px',color:'var(--text-muted)'}}>— {e.desc}</span>
                    </div>
                  ))}
                  <div style={{marginTop:'10px',padding:'8px 10px',borderRadius:'6px',background:'var(--bg-card)',border:'1px solid var(--border)',fontSize:'11.5px',color:'var(--text-muted)',lineHeight:1.7}}>
                    <strong style={{color:'var(--text-primary)'}}>Rules:</strong> Always uppercase · Use hyphens only · Number always last · No spaces · Keep it short but readable
                  </div>
                </div>
              </div>

              <button onClick={downloadSample} style={{...obtn,display:'flex',alignItems:'center',gap:'7px',marginBottom:'20px'}}>
                ↓ Download sample CSV template
              </button>

              <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={e=>e.target.files[0]&&handleFile(e.target.files[0])}/>
              <div onClick={()=>fileRef.current?.click()}
                onDragOver={e=>e.preventDefault()}
                onDrop={e=>{e.preventDefault();e.dataTransfer.files[0]&&handleFile(e.dataTransfer.files[0])}}
                style={{border:'2px dashed var(--border)',borderRadius:'var(--radius-lg)',padding:'40px',textAlign:'center',cursor:'pointer',background:'var(--bg-muted)'}}
                onMouseEnter={e=>e.currentTarget.style.borderColor='var(--primary)'}
                onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}>
                <div style={{fontSize:'32px',marginBottom:'10px'}}>📂</div>
                <div style={{fontSize:'14px',fontWeight:600,color:'var(--text-primary)'}}>Click or drag CSV file here</div>
                <div style={{fontSize:'12px',color:'var(--text-muted)',marginTop:'4px'}}>Only .csv files accepted</div>
              </div>
            </div>
          )}

          {/* STEP 1 — Preview */}
          {step===1&&(
            <div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px',marginBottom:'16px'}}>
                {[
                  {label:'Total rows',    value:activeRows.length, color:'var(--primary)'},
                  {label:'Ready',         value:validRows.length,  color:'var(--success)'},
                  {label:'Errors',        value:errorRows.length,  color:'var(--error)'},
                  {label:'Duplicates',    value:dupeRows.length,   color:'var(--warning)'},
                ].map(s=>(
                  <div key={s.label} style={{padding:'12px',borderRadius:'var(--radius-md)',border:'1px solid var(--border)',background:'var(--bg-card)'}}>
                    <div style={{fontSize:'11px',color:'var(--text-muted)',marginBottom:'4px'}}>{s.label}</div>
                    <div style={{fontSize:'22px',fontWeight:800,color:s.color}}>{s.value}</div>
                  </div>
                ))}
              </div>

              {(errorRows.length>0||dupeRows.length>0)&&(
                <div style={{display:'flex',gap:'8px',marginBottom:'12px',flexWrap:'wrap'}}>
                  {errorRows.length>0&&<button onClick={removeAllErrors} style={{...obtn,fontSize:'12px',padding:'6px 12px',color:'var(--error)'}}>Remove all {errorRows.length} error rows</button>}
                  {dupeRows.length>0&&<button onClick={removeAllDupes}  style={{...obtn,fontSize:'12px',padding:'6px 12px',color:'var(--warning)'}}>Remove all {dupeRows.length} duplicates</button>}
                </div>
              )}

              <div style={{border:'1px solid var(--border)',borderRadius:'var(--radius-md)',overflow:'hidden'}}>
                <div style={{display:'grid',gridTemplateColumns:'36px 1fr 110px 70px 70px 70px 70px',padding:'8px 12px',background:'var(--bg-muted)',borderBottom:'1px solid var(--border)',fontSize:'10px',fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',gap:'8px'}}>
                  <div>#</div><div>Name / Variant</div><div>Code</div><div>Sell</div><div>Buy</div><div>Stock</div><div>Actions</div>
                </div>
                <div style={{maxHeight:'360px',overflowY:'auto'}}>
                  {activeRows.map(row=>{
                    const {errors,warnings,isDuplicate}=row._validation
                    const hasErr=errors.length>0
                    const bg=hasErr?'var(--error-light)':isDuplicate?'#FFFBEB':warnings.length>0?'rgba(14,165,233,0.04)':'var(--bg-card)'
                    return (
                      <div key={row._rowNum} style={{display:'grid',gridTemplateColumns:'36px 1fr 110px 70px 70px 70px 70px',padding:'9px 12px',borderBottom:'1px solid var(--border)',background:bg,alignItems:'center',gap:'8px'}}>
                        <div style={{fontSize:'11px',color:'var(--text-muted)'}}>{row._rowNum}</div>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:'12.5px',fontWeight:600,color:'var(--text-primary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{row.name||'—'}</div>
                          {row.variant_name&&<div style={{fontSize:'11px',color:'var(--text-muted)'}}>{row.variant_name}</div>}
                          {hasErr&&errors.map((e,i)=><div key={i} style={{fontSize:'10.5px',color:'var(--error)',marginTop:'2px'}}>✗ {e}</div>)}
                          {isDuplicate&&<div style={{fontSize:'10.5px',color:'var(--warning)',marginTop:'2px'}}>⚠ Duplicate product code</div>}
                          {!hasErr&&!isDuplicate&&warnings.map((w,i)=><div key={i} style={{fontSize:'10.5px',color:'var(--primary)',marginTop:'2px'}}>ℹ {w}</div>)}
                        </div>
                        <div style={{fontSize:'11px',fontFamily:'var(--font-mono)',color:'var(--primary)',overflow:'hidden',textOverflow:'ellipsis'}}>{row.product_code}</div>
                        <div style={{fontSize:'11.5px'}}>৳{row.sell_price}</div>
                        <div style={{fontSize:'11.5px'}}>৳{row.buy_price}</div>
                        <div style={{fontSize:'11.5px'}}>{row.stock}</div>
                        <div style={{display:'flex',gap:'4px'}}>
                          <button onClick={()=>startEdit(row)} style={{padding:'4px 8px',border:'1px solid var(--border)',borderRadius:'6px',background:'var(--bg-card)',cursor:'pointer',fontSize:'11px',color:'var(--primary)'}}>Edit</button>
                          <button onClick={()=>removeRow(row._rowNum)} style={{padding:'4px 8px',border:'1px solid var(--error-border)',borderRadius:'6px',background:'var(--error-light)',cursor:'pointer',fontSize:'11px',color:'var(--error)'}}>✕</button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 — Confirm */}
          {step===2&&(
            <div>
              <div style={{padding:'20px',borderRadius:'var(--radius-lg)',background:'var(--success-light)',border:'1px solid var(--success-border)',marginBottom:'20px'}}>
                <div style={{fontSize:'15px',fontWeight:700,color:'var(--success)',marginBottom:'8px'}}>Ready to import</div>
                <div style={{fontSize:'13px',color:'var(--success)',lineHeight:1.8}}>
                  <div>✓ {validRows.length} rows will be imported</div>
                  <div>✓ Products grouped by product_code — variants created automatically</div>
                  <div>✓ All rows tagged as bulk_import</div>
                  {removed.size>0&&<div style={{color:'var(--text-muted)',marginTop:'4px'}}>⊘ {removed.size} rows excluded</div>}
                </div>
              </div>
              <div style={{padding:'14px',borderRadius:'var(--radius-md)',background:'#FFFBEB',border:'1px solid #FDE68A',fontSize:'12.5px',color:'#92400E'}}>
                <strong>This cannot be undone.</strong> Products will appear in Inventory immediately after import.
              </div>
            </div>
          )}

          {/* STEP 3 — Done */}
          {step===3&&result&&(
            <div style={{padding:'10px 0'}}>
              <div style={{textAlign:'center',marginBottom:'20px'}}>
                <div style={{fontSize:'48px',marginBottom:'12px'}}>{result.failed===0?'✅':'⚠️'}</div>
                <div style={{fontSize:'18px',fontWeight:700,color:'var(--text-primary)',marginBottom:'8px'}}>
                  {result.failed===0?'Import complete!':'Import finished with some errors'}
                </div>
                <div style={{fontSize:'13px',color:'var(--text-muted)',lineHeight:1.8}}>
                  <div style={{color:'var(--success)'}}>✓ {result.imported} products imported successfully</div>
                  {result.failed>0&&<div style={{color:'var(--error)'}}>✗ {result.failed} failed</div>}
                  {result.skipped>0&&<div>⊘ {result.skipped} rows skipped</div>}
                </div>
              </div>

              {result.imported>0&&(
                <div style={{padding:'16px',borderRadius:'var(--radius-lg)',background:'var(--primary-light)',border:'1px solid var(--primary-border)',marginBottom:'14px'}}>
                  <div style={{fontSize:'13px',fontWeight:700,color:'var(--primary)',marginBottom:'8px'}}>⭐ Your products are live — but they're not complete yet</div>
                  <div style={{fontSize:'12.5px',color:'var(--text-primary)',lineHeight:1.7,marginBottom:'10px'}}>
                    Bulk import gets your core data in fast. Now visit each product in Inventory to complete the missing details — the more complete your products are, the better your business runs on PageConnect.
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                    {[
                      { icon:'🖼️', label:'Product images & post photos', desc:'Customers can\'t see what they\'re buying without photos' },
                      { icon:'📐', label:'Size options & quantities per size', desc:'S/M/L/XL stock levels need to be set per variant' },
                      { icon:'💳', label:'Advance payment settings', desc:'Set if you require upfront payment for this product' },
                      { icon:'🏷️', label:'Offers & discounts', desc:'Add offer price, label, and validity dates' },
                      { icon:'🧾', label:'VAT/tax settings', desc:'Enable if this product is taxable' },
                      { icon:'📦', label:'Barcode & additional details', desc:'For POS scanning and extra product information' },
                    ].map(item=>(
                      <div key={item.label} style={{display:'flex',alignItems:'flex-start',gap:'10px',padding:'8px 10px',borderRadius:'var(--radius-sm)',background:'var(--bg-card)'}}>
                        <span style={{fontSize:'16px',flexShrink:0}}>{item.icon}</span>
                        <div>
                          <div style={{fontSize:'12.5px',fontWeight:600,color:'var(--text-primary)'}}>{item.label}</div>
                          <div style={{fontSize:'11.5px',color:'var(--text-muted)',marginTop:'1px'}}>{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{marginTop:'12px',padding:'10px 12px',borderRadius:'var(--radius-sm)',background:'var(--bg-card)',border:'1px solid var(--primary-border)',fontSize:'12px',color:'var(--primary)',fontWeight:600,lineHeight:1.6}}>
                    💡 Sellers who complete all product details get significantly better results — more conversions, fewer customer questions, and more accurate analytics. Take the time to do it right.
                  </div>
                </div>
              )}

              {result.errors.length>0&&(
                <div style={{padding:'12px',borderRadius:'var(--radius-md)',background:'var(--error-light)',border:'1px solid var(--error-border)',maxHeight:'150px',overflowY:'auto'}}>
                  <div style={{fontSize:'11.5px',fontWeight:700,color:'var(--error)',marginBottom:'6px'}}>Failed rows:</div>
                  {result.errors.map((e,i)=><div key={i} style={{fontSize:'11.5px',color:'var(--error)',marginBottom:'4px'}}>{e}</div>)}
                </div>
              )}
            </div>
          )}

          {/* Importing progress */}
          {importing&&(
            <div style={{textAlign:'center',padding:'20px'}}>
              <div style={{fontSize:'14px',fontWeight:600,color:'var(--text-primary)',marginBottom:'16px'}}>Importing products... please wait, do not close this window</div>
              <div style={{height:'8px',borderRadius:'var(--radius-full)',background:'var(--bg-muted)',overflow:'hidden',marginBottom:'8px'}}>
                <div style={{height:'100%',width:progress+'%',background:'var(--primary)',borderRadius:'var(--radius-full)',transition:'width 0.3s'}}/>
              </div>
              <div style={{fontSize:'12px',color:'var(--text-muted)'}}>{progress}% complete</div>
            </div>
          )}
        </div>

        {/* Edit overlay */}
        {editRow&&(
          <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:500}}>
            <div style={{background:'var(--bg-card)',borderRadius:'var(--radius-xl)',padding:'24px',width:'600px',maxHeight:'80vh',overflowY:'auto',border:'1px solid var(--border)',boxShadow:'0 24px 64px rgba(0,0,0,0.3)'}}>
              <div style={{fontSize:'14px',fontWeight:700,color:'var(--text-primary)',marginBottom:'16px'}}>Edit Row {editRow}</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',marginBottom:'16px'}}>
                {Object.entries(editVals).filter(([k])=>!k.startsWith('_')).map(([key,val])=>(
                  <div key={key}>
                    <label style={{fontSize:'11px',fontWeight:600,color:REQUIRED.includes(key)?'var(--error)':'var(--text-muted)',display:'block',marginBottom:'3px'}}>
                      {key}{REQUIRED.includes(key)?' *':''}
                    </label>
                    <input value={val} onChange={e=>setEditVals(p=>({...p,[key]:e.target.value}))}
                      style={{width:'100%',border:'1px solid var(--border)',borderRadius:'6px',padding:'6px 9px',fontSize:'12px',outline:'none',background:'var(--bg-card)',color:'var(--text-primary)',boxSizing:'border-box'}}/>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',gap:'10px'}}>
                <button onClick={()=>setEditRow(null)} style={obtn}>Cancel</button>
                <button onClick={saveEdit} style={btn('var(--primary)')}>Save changes</button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        {!importing&&(
          <div style={{padding:'14px 24px',borderTop:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',flexShrink:0,background:'var(--bg-muted)'}}>
            <div style={{fontSize:'12px',color:'var(--text-muted)'}}>
              {step===1&&`${activeRows.length} rows · ${validRows.length} ready · ${errorRows.length+dupeRows.length} issues`}
              {step===2&&`${validRows.length} products will be imported`}
            </div>
            <div style={{display:'flex',gap:'10px'}}>
              {step===3
                ? <button onClick={onClose} style={btn('var(--primary)')}>Close</button>
                : <>
                    {step>0&&<button onClick={()=>setStep(s=>s-1)} style={obtn}>← Back</button>}
                    {step===0&&<button onClick={onClose} style={obtn}>Cancel</button>}
                    {step===1&&(
                      <button onClick={()=>canProceed&&setStep(2)} disabled={!canProceed}
                        style={{...btn(canProceed?'var(--primary)':'var(--border)',canProceed?'white':'var(--text-muted)'),cursor:canProceed?'pointer':'not-allowed'}}>
                        {canProceed?'Continue →':`Fix ${errorRows.length+dupeRows.length} issues first`}
                      </button>
                    )}
                    {step===2&&(
                      <button onClick={handleImport} style={btn('var(--success)')}>
                        Import {validRows.length} products
                      </button>
                    )}
                  </>
              }
            </div>
          </div>
        )}
      </div>
    </div>
  )
}